import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, Container, TextField, Typography, InputAdornment, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import swal from 'sweetalert';
import { login } from '../store/ducks/auth';
import Api from '../utils/api';

const Login = () => {
  const navigate = useNavigate();
  const api = new Api();
  const dispatch = useDispatch();
  const [fgSenha, setFgSenha] = useState(false);
  const videoRef = useRef(null);

  /* eslint-disable consistent-return */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const START_AT = 26; // segundos

    const onLoaded = () => {
      // Só busca quando for possível (iOS é chato com isso)
      if (v.seekable && v.seekable.length > 0) {
        v.currentTime = Math.min(START_AT, v.duration || START_AT);
      }
      v.play().catch(() => {}); // precisa estar muted para não bloquear
    };

    if (v.readyState >= 1) onLoaded(); // HAVE_METADATA
    else v.addEventListener('loadedmetadata', onLoaded);

    return () => v.removeEventListener('loadedmetadata', onLoaded);
  }, []);

  const post = async (values, actions) => {
    actions.setSubmitting(true);

    try {
      if (!values.email) {
        actions.setFieldError('email', 'Campo obrigatório');
        return false;
      }

      if (!values.password) {
        actions.setFieldError('password', 'Campo obrigatório');
        return false;
      }

      const param = {
        email: values.email,
        password: values.password
      };

      // Faz login
      const loginResponse = await api.post('/users/login', param);

      if (!loginResponse || !loginResponse.data || !loginResponse.data.access_token) {
        throw new Error('Login inválido');
      }

      const token = loginResponse.data.access_token;

      // Busca dados do usuário
      const userResponse = await api.get('/users/me', token);

      if (!userResponse || !userResponse.data) {
        throw new Error('Erro ao buscar dados do usuário');
      }

      const userData = userResponse.data;

      // Determina o perfil baseado no is_admin
      const perfil = userData.is_admin ? 'admin' : 'affiliate';

      console.log('userData', userData);

      // Faz dispatch do login
      dispatch(login(userData, perfil, token));

      // swal('Sucesso!', 'Login realizado com sucesso!', 'success');

      setTimeout(() => {
        navigate('/app/dashboard');
      }, 1000);
    } catch (e) {
      console.error(e);
      const errorMessage = e.response?.data?.message || e.message || 'Credenciais inválidas';
      swal('Erro', errorMessage, 'error');
      actions.setFieldError('password', 'Verifique suas credenciais');
    } finally {
      actions.setSubmitting(false);
    }

    return true;
  };

  return (
    <>
      <Helmet>
        <title>Login | Sistema de Afiliados</title>
      </Helmet>
      <section
        style={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'hidden'
        }}
      >
        {/* <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          // poster="/imgs/bg.jpg"
          aria-hidden="true"
          // dica: deixe temporariamente "controls" para testar carregamento
          // controls
          src={videoPilotSelf} // pode usar src direto (sem <source>)
          style={{
            position: 'absolute',
            inset: 0, // top/right/bottom/left: 0
            width: '100%',
            height: '100%',
            objectFit: 'cover', // cobre toda a área
            zIndex: 0,
            pointerEvents: 'none'
          }}
        /> */}

        {/* overlay translúcido (se estiver opaco, o vídeo some) */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,.25), rgba(105, 80, 162, 0.5))',
            zIndex: 1
          }}
        />

        {/* seu conteúdo acima do overlay */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              width: '100vw',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Container
              maxWidth="sm"
              sx={{ backgroundColor: 'rgba(255,255,255,0.85)', borderRadius: 2, p: 2, boxShadow: 5 }}
            >
              <Formik
                initialValues={{
                  email: '',
                  password: ''
                }}
                validationSchema={Yup.object().shape({
                  email: Yup.string().email('Email inválido').required('Email é obrigatório'),
                  password: Yup.string().min(6, 'Senha deve ter no mínimo 6 caracteres').required('Senha é obrigatória')
                })}
                onSubmit={post}
              >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                  <form onSubmit={handleSubmit}>
                    <Box
                      sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        alt="Logo"
                        src="/static/logo.png"
                        style={{
                          width: 'auto',
                          height: 75,
                          resize: 'contain'
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Typography color="textPrimary" variant="h2">
                        Login
                      </Typography>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Sistema de Afiliados
                      </Typography>
                    </Box>

                    <TextField
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label="Email"
                      margin="normal"
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      value={values.email}
                      variant="outlined"
                      inputProps={{
                        style: {
                          backgroundColor: 'white'
                        }
                      }}
                    />
                    <TextField
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Senha"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={!fgSenha ? 'password' : 'text'}
                      value={values.password}
                      variant="outlined"
                      InputProps={{
                        style: {
                          backgroundColor: 'white'
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setFgSenha(!fgSenha)}>
                              {!fgSenha ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Box sx={{ py: 2 }}>
                      <Button
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        {isSubmitting ? `Processando, por favor aguarde...` : `Entrar`}
                      </Button>
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Link
                        to="/forgot-password"
                        style={{ color: '#5048E5', textDecoration: 'none', fontSize: '14px' }}
                      >
                        Esqueci minha senha
                      </Link>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2">
                        Não tem uma conta?{' '}
                        <Link to="/#cadastro" style={{ color: '#5048E5', textDecoration: 'none', fontWeight: 600 }}>
                          Criar conta
                        </Link>
                      </Typography>
                    </Box>
                  </form>
                )}
              </Formik>
            </Container>
          </Box>
        </div>
      </section>
    </>
  );
};

export default Login;
